import { metrics } from '@opentelemetry/api';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { getWeaveToken } from '@frontend/auth-helpers';

export const DEFAULT_OTEL_TIMEOUT_MILLIS = 5000;

export interface OtelConfig {
  endpoint: string;
  serviceName?: string;
  headers?: Record<string, string>;
  getAuthToken?: () => string | undefined | Promise<string | undefined>;
  timeoutMillis?: number;
}

class OpenTelemetryClient {
  private meterProvider: MeterProvider | null = null;
  private meter: any = null;
  private counters = new Map<string, any>();
  private histograms = new Map<string, any>();

  /**
   * Initializes OpenTelemetry metrics SDK with OTLP/HTTP export.
   */
  public init(config: OtelConfig) {
    try {
      // Avoid re-initialization if already initialized
      if (this.meterProvider) {
        return;
      }

      const timeoutMillis = config.timeoutMillis ?? DEFAULT_OTEL_TIMEOUT_MILLIS;

      const exporter = new OTLPMetricExporter({
        url: config.endpoint,
        timeoutMillis,
        // Resolved per-export so the token stays fresh.
        headers: async () => {
          const token = config.getAuthToken ? await config.getAuthToken() : getWeaveToken();
          const authorization = token ? `Bearer ${token}` : undefined;
          return {
            ...config.headers,
            ...(authorization ? { Authorization: authorization } : {}),
          };
        },
      });

      const metricReader = new PeriodicExportingMetricReader({
        exporter,
        exportIntervalMillis: 60000, // Ship metrics every 60 seconds
        exportTimeoutMillis: timeoutMillis,
      });

      this.meterProvider = new MeterProvider({
        resource: resourceFromAttributes({
          'service.name': config.serviceName || 'weave-frontend',
        }),
        readers: [metricReader],
      });

      // Register globally with OTEL API
      metrics.setGlobalMeterProvider(this.meterProvider);

      // Instantiates our primary tracking meter
      this.meter = metrics.getMeter('@weave/tracking');
    } catch (err) {
      console.error('Failed to initialize OpenTelemetry Metrics:', err);
    }
  }

  /**
   * Record a metric. `count` increments a counter; `distribution` records a value to a histogram so
   * percentiles can be computed.
   */
  public metric(
    name: string,
    value: number,
    attributes?: Record<string, string | number | boolean>,
    options?: { type?: 'count' | 'distribution'; unit?: string }
  ) {
    if (!this.meter) {
      if (import.meta.env.DEV) {
        console.warn(`OTEL metric "${name}" skipped because OTEL SDK is not initialized.`);
      }
      return;
    }

    try {
      if (options?.type === 'distribution') {
        let histogram = this.histograms.get(name);
        if (!histogram) {
          histogram = this.meter.createHistogram(name, {
            description: `Custom telemetry histogram: ${name}`,
            ...(options.unit ? { unit: options.unit } : {}),
          });
          this.histograms.set(name, histogram);
        }
        histogram.record(value, attributes);
        return;
      }

      let counter = this.counters.get(name);
      if (!counter) {
        counter = this.meter.createCounter(name, {
          description: `Custom telemetry counter: ${name}`,
        });
        this.counters.set(name, counter);
      }

      counter.add(value, attributes);
    } catch (err) {
      console.error(`Error recording OTEL metric "${name}":`, err);
    }
  }

  /**
   * Shuts down metric readers and provider
   */
  public async shutdown() {
    if (this.meterProvider) {
      await this.meterProvider.shutdown();
      this.meterProvider = null;
      this.meter = null;
      this.counters.clear();
      this.histograms.clear();
    }
  }
}

export const otel = new OpenTelemetryClient();
